
import { logger } from './logger';

/**
 * Extract file name and line number from error stack if possible
 */
export function extractFileInfoFromStack(stack?: string): { fileName?: string, lineNumber?: number } {
  if (!stack) return {};
  
  // Look for common stack trace patterns like "at ComponentName (file:line:column)"
  const stackLines = stack.split('\n');
  for (const line of stackLines) {
    const match = line.match(/at\s+.+\s+\((.+):(\d+):(\d+)\)/);
    if (match) {
      return {
        fileName: match[1],
        lineNumber: parseInt(match[2], 10)
      };
    }
  }
  
  return {};
}

/**
 * Enhanced error logging with context information
 */
export function logComponentError(
  componentName: string, 
  error: Error, 
  errorInfo?: React.ErrorInfo
): void {
  // Enhanced error logging with file information if available
  const errorWithStack = error as Error & { fileName?: string, lineNumber?: number };
  const fileInfo = errorWithStack.fileName ? 
    { fileName: errorWithStack.fileName, lineNumber: errorWithStack.lineNumber } : 
    extractFileInfoFromStack(error.stack);
  
  // Log the error with enhanced context
  logger.error(`Error in ${componentName}:`, error, {
    component: componentName,
    context: {
      stack: errorInfo?.componentStack,
      ...fileInfo,
      timestamp: new Date().toISOString()
    }
  });
}

/**
 * Report error to error tracking system
 */
export function reportError(componentName: string, error: Error, errorInfo?: React.ErrorInfo): void {
  const errorWithStack = error as Error & { fileName?: string, lineNumber?: number };
  const fileInfo = errorWithStack.fileName ? 
    { fileName: errorWithStack.fileName, lineNumber: errorWithStack.lineNumber } : 
    extractFileInfoFromStack(error?.stack);
  
  const errorData = {
    component: componentName,
    message: error?.message || 'Unknown error',
    stack: error?.stack,
    componentStack: errorInfo?.componentStack,
    ...fileInfo,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };
  
  // Log as user-reported error
  logger.error(`User reported error in ${componentName}`, error, {
    component: componentName,
    context: errorData,
    userReported: true,
    timestamp: new Date()
  });
  
  console.log('Reporting error:', errorData);
}

/**
 * Safe JSON parsing with enhanced error handling and detailed diagnostics
 */
export async function safeJsonParse<T>(response: Response): Promise<T> {
  // First check if the response is OK
  if (!response.ok) {
    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('text/html')) {
      // Get the full HTML content for detailed logging
      const htmlContent = await response.text();
      
      // Log the full HTML content for debugging
      console.error('Received HTML instead of JSON response:');
      console.error('Status:', response.status, response.statusText);
      console.error('URL:', response.url);
      console.error('Content preview:', htmlContent.substring(0, 500));
      
      // Check if this is a CORS error page
      if (htmlContent.includes('Cross-Origin Request Blocked') || 
          htmlContent.includes('CORS policy')) {
        throw new Error('CORS error: API request blocked due to cross-origin restrictions');
      }
      
      // Check if this looks like an authentication error
      if (htmlContent.includes('authentication') || 
          htmlContent.includes('unauthorized') ||
          htmlContent.includes('api key') ||
          response.status === 401 ||
          response.status === 403) {
        throw new Error('Authentication error: Invalid or missing API key');
      }
      
      throw new Error(`Received HTML error page instead of JSON response (HTTP ${response.status})`);
    }
    
    // Try to get error details from response
    const errorText = await response.text();
    console.error('Error response status:', response.status, response.statusText);
    console.error('Error response headers:', Object.fromEntries(response.headers.entries()));
    console.error('Error response body:', errorText.substring(0, 500));
    
    try {
      const errorData = JSON.parse(errorText);
      throw new Error(errorData.message || errorData.error || `API Error: ${response.status} ${response.statusText}`);
    } catch (jsonError) {
      // If we can't parse as JSON, use text or status
      if (errorText) {
        throw new Error(`API Error: ${errorText.substring(0, 100)}...`);
      } else {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
    }
  }
  
  // Get the text first to handle empty responses
  const text = await response.text();
  console.log('Raw API response:', text.substring(0, 200) + (text.length > 200 ? '...' : ''));
  
  // Check if the response is completely empty
  if (!text || text.trim() === '') {
    console.error('Received empty response from API');
    throw new Error('API returned an empty response');
  }
  
  // Log the content type for debugging
  const contentType = response.headers.get('Content-Type');
  if (!contentType || !contentType.includes('application/json')) {
    console.error('Invalid content type:', contentType);
    console.error('Response preview:', text.substring(0, 200));
    
    // If it's HTML, log it specially
    if (text.includes('<html') || text.includes('<!DOCTYPE html')) {
      console.error('Received HTML when expecting JSON. First 500 chars:');
      console.error(text.substring(0, 500));
      throw new Error(`Received HTML page instead of JSON (content-type: ${contentType})`);
    }
    
    throw new Error(`Invalid content type: ${contentType}. Expected JSON.`);
  }
  
  // Parse the JSON
  try {
    return JSON.parse(text) as T;
  } catch (parseError) {
    console.error('JSON parse error:', parseError);
    console.error('Response text:', text.substring(0, 500));
    logger.error('Failed to parse JSON response', parseError as Error, {
      context: {
        responsePreview: text.substring(0, 500),
        contentType,
        status: response.status,
        url: response.url
      }
    });
    throw new Error(`Failed to parse JSON response: ${(parseError as Error).message}`);
  }
}

/**
 * Safely get content from API response, with fallback
 */
export async function safeGetContent<T>(
  response: Response, 
  fallbackValue?: T,
  logPrefix: string = 'API'
): Promise<T> {
  try {
    return await safeJsonParse<T>(response);
  } catch (error) {
    // Log the error
    console.error(`${logPrefix} Error:`, error);
    logger.error(`${logPrefix} Error`, error as Error);
    
    // Return fallback if provided, otherwise rethrow
    if (fallbackValue !== undefined) {
      return fallbackValue;
    }
    throw error;
  }
}
